import { assertEvent, assign } from 'xstate';

import { removeTokens, storeEmbeddedFlowTokens } from '../../../../../../context/AzureAuth';
import {
  type Action,
  type MessageParams,
  type RefreshAccessTokensOutput,
  type SystemErrorParams,
} from '../join-or-sign-in-machine.types';
import { logger } from './join-or-sign-in-machine.logger';

// ─── Internal Actions ────────────────────────────────────────────────────────

const storeTokens: Action = ({ context }) => {
  const { refreshToken } = context;

  if (!refreshToken) return;

  void storeEmbeddedFlowTokens({ refreshToken });
};

const removeRefreshToken: Action = () => {
  void removeTokens();
};

const storeEmail: Action = assign(({ event }) => {
  assertEvent(event, ['TOKEN_RECEIVED', 'VERIFICATION_CODE_LOADED']);

  return { email: event.email };
});

const storeToken: Action = assign(({ event }) => {
  assertEvent(event, 'TOKEN_RECEIVED');

  return {
    accessToken: event.apiAccessToken,
    refreshToken: event.apiRefreshToken,
    email: event.email,
  };
});

const storeAccessTokens: Action<{ output: RefreshAccessTokensOutput }> = assign((_args, params) => {
  const { output } = params;

  if (!output) return {};

  return { accessToken: output.apiAccessToken };
});

const storeAccountInput: Action = assign(({ event }) => {
  assertEvent(event, 'SUBMIT_ACCOUNT_DATA');

  return { createAccountInput: event.input };
});

// ─── External Actions ────────────────────────────────────────────────────────

const onDebugMessage: Action<{ data: Record<string, unknown> | undefined }> = () => {
  logger.debug('`onDebugMessage` action is not provided.');
};

const onInitError: Action<{ data: string | undefined }> = () => {
  logger.debug('`onInitError` action is not provided.');
};

const onInitTimeoutError: Action = () => {
  logger.debug('`onInitTimeoutError` action is not provided.');
};

const onVerificationScreenLoadingTimeout: Action = () => {
  logger.debug('`onVerificationScreenLoadingTimeout` action is not provided.');
};

const onVerificationTimeout: Action = () => {
  logger.debug('`onVerificationTimeout` action is not provided.');
};

const onSignInSystemError: Action<SystemErrorParams> = () => {
  logger.debug('`onSignInSystemError` action is not provided.');
};

const onRefreshAccessTokensSystemError: Action<SystemErrorParams> = () => {
  logger.debug('`onRefreshAccessTokensSystemError` action is not provided.');
};

const onCreateAccountSystemError: Action<SystemErrorParams> = () => {
  logger.debug('`onCreateAccountSystemError` action is not provided.');
};

const onErrorMessage: Action<MessageParams> = () => {
  logger.debug('`onErrorMessage` action is not provided.');
};

const onSuccessMessage: Action<MessageParams> = () => {
  logger.debug('`onSuccessMessage` action is not provided.');
};

// ─────────────────────────────────────────────────────────────────────────────

export const Actions = {
  storeTokens,
  removeRefreshToken,
  storeEmail,
  storeToken,
  storeAccessTokens,
  storeAccountInput,

  onDebugMessage,
  onInitError,
  onInitTimeoutError,
  onVerificationScreenLoadingTimeout,
  onVerificationTimeout,
  onSignInSystemError,
  onRefreshAccessTokensSystemError,
  onCreateAccountSystemError,
  onErrorMessage,
  onSuccessMessage,
};
